import { Component, Host, h, Prop, State, Watch } from "@stencil/core";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/nl";
import {
  ILiftStatusWebComponentError,
  ILiftStatusSensorDataResponse,
  SensorId,
} from "../../utils/types";
import { sensorInfo } from "../../utils/sensorInfo";
import { includes, isEmpty, isNil } from "lodash-es";
import { getStore, ILiftStatusStore } from "../../store";
import { getSafeRelativeTime } from "../../utils/safe-relative-time";
import { watchStoreLocale } from "../../utils/locale-state";
import { getStoreUnavailableError } from "../../utils/store-error";

@Component({
  tag: "ls-status-item",
  styleUrl: "ls-status-item.css",
  shadow: true,
})
export class LsStatusItem {
  private static readonly SENSOR_ID_GEZOND_A = 10;
  private static readonly SENSOR_ID_GEZOND_B = 524;
  /**
   * The ID of the sensor item that is to be displayed.
   */
  @Prop() readonly sensorId: number;

  private store: ILiftStatusStore | null = null;
  private isComponentMounted = false;
  private storeUnsubscribe: (() => void) | null = null;
  private localeUnsubscribe: (() => void) | null = null;

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * Selects which label set to use for the sensor label.
   */
  @Prop() readonly labelType: "default" | "short" = "default";

  /**
   * Specifies the IDs and labels of the sensors that are allowed to be displayed.
   */
  private allowedIds = "10,23,500,501,502,503,514,515,524,590";

  private sensorsArray = this.allowedIds
    .split(",")
    .filter((id) => id.trim() !== "")
    .map((id) => parseInt(id.trim(), 10));

  /**
   * Handles a change of the sensor ID
   */
  @Watch("sensorId")
  watchPropHandler() {
    // Clean up before reloading
    if (this.storeUnsubscribe !== null) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }
    if (this.localeUnsubscribe !== null) {
      this.localeUnsubscribe();
      this.localeUnsubscribe = null;
    }
    this.clearTimer();
    this.componentWillLoad();
  }

  /**
   * The data received from the subscription is placed in this state.
   */
  @State() sensorData: string;

  /**
   * The timestamp of the last update
   */
  @State() timestamp: string;

  /**
   * Centralized error state for tracking component errors
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: "",
  };

  /**
   * Keeps track of the setInterval ID.
   */
  private timer: number | null;

  private get isValidSensorId(): boolean {
    return this.sensorsArray.includes(this.sensorId);
  }

  private get dataLabel(): string {
    if (this.sensorId === null || this.sensorId === undefined) {
      return "";
    }

    if (!this.isValidSensorId) {
      return this.store.t("ui:sensor.unknown_id");
    }

    const baseLabel =
      this.labelType === "short"
        ? sensorInfo.getShortLabel(this.sensorId, this.store?.state.locale)
        : sensorInfo.getLabel(this.sensorId, this.store?.state.locale);

    if (
      [
        LsStatusItem.SENSOR_ID_GEZOND_A,
        LsStatusItem.SENSOR_ID_GEZOND_B,
      ].includes(this.sensorId)
    ) {
      const labelSuffixOk = this.store.t("ui:sensor.label_suffix_ok");
      const labelSuffixHealthy = this.store.t("ui:sensor.label_suffix_healthy");
      if (this.labelType === "short") {
        return baseLabel.replace(labelSuffixOk, "");
      }
      return baseLabel.replace(labelSuffixHealthy, "");
    }

    return baseLabel;
  }

  /**
   * Updates the error state based on current component conditions
   */
  private updateErrorState() {
    if (this.store === null) {
      this.errorState = getStoreUnavailableError(this.store?.state.locale);
      return;
    }

    if (this.store.state.error.hasError) {
      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };
      this.clearTimer();
      return;
    }

    // No error detected
    this.errorState = {
      hasError: false,
      messageKey: undefined,
      type: "",
    };
  }

  private updateData = (sensorData: ILiftStatusSensorDataResponse) => {
    // Don't update if component is unmounted
    if (!this.isComponentMounted) return;

    // Reset timer to prevent waiting on next timer tick
    this.clearTimer();
    this.setTimer();

    // If sensorData is empty, we don't need to update the data
    if (isEmpty(sensorData)) return;

    // The sensor values of 501 and 514 and 515 are zero based, but we need to show them one based
    if (
      includes([501, 514, 515], this.sensorId) &&
      !isNaN(parseInt(sensorData.values[this.sensorId], 10))
    ) {
      this.sensorData = sensorData.values[this.sensorId] + 1;
    } else {
      this.sensorData = sensorData.values[this.sensorId];
    }

    // Calculate relative timestamp
    this.timestamp = getSafeRelativeTime(
      sensorData.updated[this.sensorId],
      this.store?.state.locale
    );

    this.updateErrorState();
  };

  private setTimer() {
    // Clear any existing timer first
    this.clearTimer();

    // Used to update relative timestamps
    this.timer = window.setInterval(() => {
      // Check if still mounted and store exists
      if (!this.isComponentMounted) {
        this.clearTimer();
        return;
      }

      // Access store data only when needed
      const sensorData = this.store?.state?.currentSensorData;
      if (!isNil(sensorData)) {
        this.updateData(sensorData);
      }
    }, 20000);
  }

  private clearTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  connectedCallback() {
    this.isComponentMounted = true;
    this.setTimer();
  }

  disconnectedCallback() {
    this.isComponentMounted = false;
    this.clearTimer();

    // Unsubscribe from store changes
    if (this.storeUnsubscribe !== null) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }
    if (this.localeUnsubscribe !== null) {
      this.localeUnsubscribe();
      this.localeUnsubscribe = null;
    }

  }

  async componentWillLoad() {
    dayjs.extend(relativeTime);

    this.store = await getStore(this.idKey);
    if (this.localeUnsubscribe !== null) {
      this.localeUnsubscribe();
    }
    this.localeUnsubscribe = watchStoreLocale(this, this.store, () => {
      if (!this.isValidSensorId) {
        this.sensorData = this.store.t("ui:sensor.valid_options");
        this.timestamp = this.sensorsArray.join(", ");
        return;
      }

      this.updateData(this.store.state.currentSensorData);
    }, { skipForceUpdate: true });

    if (!this.isValidSensorId) {
      this.sensorData = this.store.t("ui:sensor.valid_options");
      this.timestamp = this.sensorsArray.join(", ");
      this.clearTimer();
      return;
    }

    // If timer is cleared we need to start it again
    if (this.timer === null) {
      this.setTimer();
    }

    this.updateData(this.store.state.currentSensorData);

    // Store the unsubscribe function for cleanup
    this.storeUnsubscribe = this.store.onChange(
      "currentSensorData",
      (sensorData) => {
        this.updateData(sensorData);
      },
    );

  }

  render() {
    const sensorIdLabel =
      this.sensorId === null || this.sensorId === undefined
        ? this.store.t("ui:sensor.not_specified")
        : this.sensorId;
    const fallbackLabel = this.store.t("ui:sensor.fallback_label", {
      id: sensorIdLabel,
    });

    return (
      <Host>
        <slot></slot>
        <dl class="ls-status-item">
          <dt class="label">
            {this.dataLabel || fallbackLabel}
          </dt>
          <dd class="value">
            {sensorInfo.getValue(
              this.sensorData,
              this.sensorId as SensorId,
              this.store?.state.locale
            ) ||
              "-"}
          </dd>
          <dd class="timestamp">{this.timestamp}</dd>
        </dl>
      </Host>
    );
  }
}
